import { CopyIcon } from 'lucide-react';
import React, { useMemo } from 'react';

import type { ThemeOption } from '@/app/ThemeContext';
import { TabOption } from '@/components/chariot-ui';
import { ProofRenderer } from '@/components/proof/ProofRenderer';
import { isStructuredProof } from '@/components/proof/types';
import { Table } from '@/components/table/Table';
import { Button } from '@/components/ui/button';
import {
  getVulnerabilityClassification,
  VulnerabilityClassification,
} from '@/sections/poe/components/VulnerabilityClassification';
import { MAIN_TAB_DEFS } from '@/sections/poe/constants/mainTabDefs';
import { processAugustusDataSource } from '@/sections/poe/processors/augustus.processor';
import { processBurpDataSource } from '@/sections/poe/processors/Burp.processor';
import { processConstantineDataSource } from '@/sections/poe/processors/constantine.processor';
import { processCrawlerDataSource } from '@/sections/poe/processors/crawler.processor';
import { renderGatoEvidence } from '@/sections/poe/processors/Gato.processor';
import { processGenericDataSource } from '@/sections/poe/processors/Generic.processor';
import { processHadrianDataSource } from '@/sections/poe/processors/hadrian.processor';
import { addLoginPanelTab } from '@/sections/poe/processors/LoginPanel.processor';
import { processNucleiDataSource } from '@/sections/poe/processors/Nuclei.processor';
import { processOrcaDataSource } from '@/sections/poe/processors/Orca.processor';
import { processPortscanDataSource } from '@/sections/poe/processors/portscan.processor';
import { processSecretsDataSource } from '@/sections/poe/processors/Secrets.processor';
import { processSnykCodeDataSource } from '@/sections/poe/processors/Snyk.processor';
import { processSshDataSource } from '@/sections/poe/processors/ssh.processor';
import { processWhoisDataSource } from '@/sections/poe/processors/whois.processor';
import { processWizDataSource } from '@/sections/poe/processors/Wiz.processor';
import type {
  ExtendedTabOption,
  GitHubPOE,
  MainTabKey,
  POEContent,
  SectionContent,
  SummaryPOE,
} from '@/sections/poe/types/poe.types';
import { createImageSection } from '@/sections/poe/utils/CreateImageSection';
import type { Attribute, RiskSeverity, Webpage } from '@/types';
import { formatDate } from '@/utils/date.util';
import { decode } from '@/utils/encrypt.util';

function getMainTabsForSource(source: string): MainTabKey[] {
  if (source.startsWith('nuclei') || source === 'cve-2025-55182') {
    return [
      'INFORMATION',
      'REQUEST',
      'RESPONSE',
      'DETECTION',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('burp')) {
    return [
      'INFORMATION',
      'REMEDIATION',
      'REQUEST',
      'RESPONSE',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('crawler')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES', 'LOGIN_PANEL'];
  }
  if (source.startsWith('login-detection') || source.startsWith('loginpanel')) {
    return ['INFORMATION', 'LOGIN_PANEL', 'ATTRIBUTES'];
  }
  if (source.startsWith('snyk')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (
    source.startsWith('secrets') ||
    source.includes('find-secrets') ||
    source.includes('-secrets')
  ) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('augustus')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('constantine')) {
    // DETECTION first: Constantine's processor only populates DETECTION with
    // findings, so INFORMATION would otherwise contain only aux-info text and
    // become an unexpected default landing tab when aux info is set.
    return ['DETECTION', 'INFORMATION', 'ATTRIBUTES'];
  }
  if (source.startsWith('orca')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('hadrian')) {
    return ['INFORMATION', 'DETECTION', 'REQUEST', 'RESPONSE', 'ATTRIBUTES'];
  }
  if (source.startsWith('wiz')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  return ['INFORMATION', 'ATTRIBUTES', 'LOGIN_PANEL'];
}

export interface UsePOESectionsParams {
  name: string;
  source: string;
  severity: RiskSeverity | undefined;
  file: unknown;
  fileStatus: 'pending' | 'success' | 'error';
  evidenceFileValue: string;
  attributes: Attribute[];
  handleCopy: (content: string) => void;
  webpage: Webpage | undefined;
  theme: 'light' | 'dark';
}

interface UsePOESectionsResult {
  sections: Record<string, SectionContent>;
  availableTabs: ExtendedTabOption[];
  copyContents: Record<string, string>;
}

export function usePOESections({
  name,
  source,
  severity,
  file,
  fileStatus,
  evidenceFileValue,
  attributes,
  handleCopy,
  webpage,
  theme,
}: UsePOESectionsParams): UsePOESectionsResult {
  return useMemo(() => {
    if (fileStatus === 'pending') {
      return { sections: {}, availableTabs: [], copyContents: {} };
    }

    const mainTabKeys = getMainTabsForSource(source);

    function buildAvailableTabs(
      tempTabs: Record<MainTabKey, TabOption[]>,
      keys: MainTabKey[]
    ): ExtendedTabOption[] {
      return keys
        .filter(
          key =>
            tempTabs[key].length > 0 ||
            key === 'ATTRIBUTES' ||
            (key === 'INFORMATION' && evidenceFileValue)
        )
        .map(key => {
          const tabDef = MAIN_TAB_DEFS[key];
          return {
            label: (
              <div className="flex items-center gap-3">
                <tabDef.icon className="size-4" />
                <span>{tabDef.label}</span>
              </div>
            ),
            heading: tabDef.heading || tabDef.label,
            value: tabDef.id,
            id: tabDef.id,
            subTabs: tempTabs[key],
          };
        }) as ExtendedTabOption[];
    }

    function buildAllTabsFromTempTabs(
      tempTabs: Record<MainTabKey, TabOption[]>
    ): ExtendedTabOption[] {
      return Object.keys(tempTabs)
        .filter(key => tempTabs[key as MainTabKey].length > 0)
        .map(key => {
          const tabDef = MAIN_TAB_DEFS[key as MainTabKey];
          return {
            label: (
              <div className="flex items-center gap-3">
                <tabDef.icon className="size-4" />
                <span>{tabDef.label}</span>
              </div>
            ),
            heading: tabDef.heading || tabDef.label,
            value: tabDef.id,
            id: tabDef.id,
            subTabs: tempTabs[key as MainTabKey],
          };
        }) as ExtendedTabOption[];
    }

    function getPOESections(): UsePOESectionsResult {
      try {
        const poeData = file as POEContent;
        const fileAsContent = file as POEContent | string;
        const sections: Record<string, SectionContent> = {};
        const copyContents: Record<string, string> = {};
        const tempTabs: Record<MainTabKey, TabOption[]> = {
          REQUEST: [],
          RESPONSE: [],
          DETECTION: [],
          ATTRIBUTES: [],
          LOGIN_PANEL: [],
          INFORMATION: [],
          REMEDIATION: [],
        };

        if (isStructuredProof(file)) {
          file.sections.forEach((section, idx) => {
            const value = `structured_proof_${idx}`;
            sections[value] = (
              <ProofRenderer
                proof={{ format: file.format, sections: [section] }}
                bare
              />
            );
            tempTabs['INFORMATION'].push({
              label: section.title || 'Evidence',
              value,
            });
          });
          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        const evidenceImage = createImageSection(
          evidenceFileValue,
          'evidence_image',
          'Uploaded Evidence'
        );
        if (evidenceImage) {
          sections['evidence_image'] = evidenceImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(evidenceImage.tabOption);
          }
        }

        const mainImage = createImageSection(
          file,
          'uploaded_image',
          'Uploaded Evidence'
        );
        if (mainImage) {
          sections['uploaded_image'] = mainImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(mainImage.tabOption);
          }
          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        if (
          source &&
          source.startsWith('gato') &&
          poeData.username &&
          poeData.enumeration &&
          Array.isArray(poeData.enumeration.repositories)
        ) {
          sections['github_evidence'] = renderGatoEvidence(
            poeData as GitHubPOE
          );
          if (tempTabs['INFORMATION']) {
            tempTabs['INFORMATION'].push({
              label: 'GitHub Evidence',
              value: 'github_evidence',
            });
          }
          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        const summary = poeData?.info
          ? typeof poeData.info === 'string'
            ? (decode(poeData.info, {}) as SummaryPOE)
            : (poeData.info as unknown as SummaryPOE)
          : undefined;

        const vulnerabilityClassificationData = getVulnerabilityClassification(
          summary,
          severity
        );
        if (vulnerabilityClassificationData.some(({ value }) => !!value)) {
          sections['vuln_classification'] = (
            <VulnerabilityClassification
              data={vulnerabilityClassificationData}
              severity={severity}
            />
          );
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push({
              label: 'Vulnerability Classification',
              value: 'vuln_classification',
            });
          }
        }

        if (source.startsWith('burp')) {
          processBurpDataSource(
            poeData,
            sections,
            tempTabs,
            mainTabKeys,
            copyContents
          );
        } else if (source.startsWith('login-detection')) {
          // Login-detection proof is rendered entirely by the Login Panel below.
        } else if (source.startsWith('nuclei') || source === 'cve-2025-55182') {
          processNucleiDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (
          source.startsWith('webpage-secrets') ||
          source.startsWith('crawler') ||
          source.startsWith('confluence-secrets')
        ) {
          processCrawlerDataSource(
            fileAsContent,
            sections,
            tempTabs,
            true,
            mainTabKeys
          );
        } else if (
          source.startsWith('secrets') ||
          source.includes('find-secrets') ||
          source.includes('-secrets')
        ) {
          processSecretsDataSource(fileAsContent, sections, tempTabs);
        } else if (source.startsWith('augustus')) {
          processAugustusDataSource(fileAsContent, sections, tempTabs);
        } else if (source.startsWith('constantine')) {
          processConstantineDataSource(fileAsContent, sections, tempTabs);
        } else if (
          source.startsWith('portscan') ||
          source.startsWith('fingerprint') ||
          name.startsWith('exposure-')
        ) {
          processPortscanDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('ssh')) {
          processSshDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('whois')) {
          processWhoisDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('orca')) {
          processOrcaDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('snyk')) {
          processSnykCodeDataSource(
            fileAsContent,
            sections,
            tempTabs,
            mainTabKeys
          );
        } else if (source.startsWith('wiz')) {
          processWizDataSource(poeData, sections, tempTabs, mainTabKeys);
        } else if (source.startsWith('hadrian')) {
          processHadrianDataSource(poeData, sections, tempTabs, copyContents);
        } else {
          processGenericDataSource(
            poeData,
            sections,
            tempTabs,
            theme as ThemeOption,
            mainTabKeys,
            copyContents
          );
        }

        if (webpage && name.startsWith('web-login')) {
          addLoginPanelTab({
            sections,
            tempTabs,
            webpage,
            poeData,
            mainTabKeys,
          });
        }

        if (attributes && attributes.length > 0) {
          const attributesTableData = [...attributes]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(attribute => ({
              name: attribute.name,
              value: attribute.value,
              lastSeen: formatDate(attribute.visited),
            }));
          sections['attributes'] = (
            <Table
              name="attributes"
              data={attributesTableData}
              columns={[
                { label: 'Name', id: 'name' },
                {
                  label: 'Value',
                  id: 'value',
                  cell: (row: {
                    name: string;
                    value: string;
                    lastSeen: string;
                  }) =>
                    row.value ? (
                      <div className="text-chariot-text-secondary flex w-fit items-center rounded px-2 py-1">
                        <span>{row.value}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Copy to clipboard"
                          onClick={() => handleCopy(row.value)}
                        >
                          <CopyIcon className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      '-'
                    ),
                },
                {
                  label: 'Last Seen',
                  id: 'lastSeen',
                  align: 'right',
                  fixedWidth: 100,
                },
              ]}
              status="success"
              error={null}
              selection={{ hide: true }}
            />
          );
          if (mainTabKeys.includes('ATTRIBUTES')) {
            const hasAttributesSubTab = tempTabs['ATTRIBUTES'].some(
              st => st.value === 'attributes'
            );
            if (!hasAttributesSubTab) {
              tempTabs['ATTRIBUTES'].push({
                label: 'Attributes',
                value: 'attributes',
              });
            }
          }
        }

        return {
          sections,
          availableTabs: buildAvailableTabs(tempTabs, mainTabKeys),
          copyContents,
        };
      } catch (error) {
        console.error('Error processing POE data:', error);
        return {
          sections: {},
          availableTabs: [] as ExtendedTabOption[],
          copyContents: {},
        };
      }
    }

    return getPOESections();
  }, [
    name,
    source,
    severity,
    file,
    fileStatus,
    evidenceFileValue,
    attributes,
    webpage,
    theme,
    handleCopy,
  ]);
}
